import { Injectable } from '@angular/core';
import { BundleType, EntityTypeId, HttpService, interceptorsToken, NodeModel, WebinarsService } from 'library-explorer';
import { LearningPathModel } from '@app/model/learning-path.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { finalize, map, mergeMap, tap } from 'rxjs/operators';
import { DataResponse } from '@app/model/data-response.model';
import { NavigateFrom } from '@app/model/enums/navigate-from.enum';
import { Router } from '@angular/router';
import { LessonService } from './lesson.service';
import { PayableNodeNavigationService } from '../payable-node-navigation.service';
import { ContentGroupService } from './content-group.service';
import { LessonDTO } from '@app/model/lessonsDTO.model';

@Injectable({
  providedIn: 'root'
})
export class LearningPathService {
  
  private readonly CONTENT_PAGER_LIMIT = 10;

  constructor(
    private webinarService: WebinarsService,
    private httpService: HttpService,
    private lessonService: LessonService,
    private payableNodeNavigationService: PayableNodeNavigationService,
    private contentGroupService: ContentGroupService,
    private router: Router
  ) { }

  public getLearningPathsForUser(id: string): Observable<DataResponse<LearningPathModel>> {
    const params = new HttpParams().set('sort', 'weight:ASC');
    return this.httpService.get<DataResponse<LearningPathModel>>(`learning-paths/user/${id}`, params);
  }

  public getLearningPath(id: string): Observable<LearningPathModel> {
    const params = new HttpParams().set('id', id);
    return this.httpService.get<LearningPathModel>('node', params).pipe(
      map(this.processLearningPath.bind(this))
    );
  }

  public getNextLearningPathItem(learningPathId: string): Observable<NodeModel> {
    return this.httpService.get<NodeModel>(`learning-path/${learningPathId}/next-in-progress`);
  }

  public processLearningPath(data: LearningPathModel): LearningPathModel {
    if (data.duration && !data.deadline && data.startAt) {
      const durationMs = data.duration * 1000 * 60 * 60 * 24;
      const deadlineMs = new Date(data.startAt).getTime() + durationMs;
      data.deadline = new Date(deadlineMs).toJSON();
    }

    if (data.deadline) {
      const deadlineMs = new Date(data.deadline).getTime();
      const dateInMs = (data.dateOfCompletion ? new Date(data.dateOfCompletion) : new Date()).getTime();
      const remainingMs = deadlineMs - new Date().getTime();
      data.overdue = deadlineMs < dateInMs;
      data.remainingMinutes = remainingMs > 0 ? Math.round(remainingMs / 1000 / 60) : 0; 
    }

    return data;
  }

  public startLearningPath(data: LearningPathModel, loading: BehaviorSubject<boolean> = new BehaviorSubject(false)): void {
    const nextStep = this.getPathNextStep(data);
    loading.next(true);

    if (!nextStep) {
      loading.next(false)
      return;
    }
    
    this.navigateToNextStep(nextStep, data, loading);
  }
  
  public getContentGroupItems(item: any, offset = 0, prevItems: any[] = []): Observable<any> {
    return this.contentGroupService.getContentGroupEntities(item.id, offset, this.CONTENT_PAGER_LIMIT).pipe(
      mergeMap(res => 
        (res._meta.maxcount > offset + this.CONTENT_PAGER_LIMIT)
        ? this.getContentGroupItems(item, offset + this.CONTENT_PAGER_LIMIT, [...prevItems, ...res.items])
        : of([...prevItems, ...res.items]))
    )
  }
  
  public getNextContentGroupItem(item: any, offset = 0, firstItem?: any): Observable<any> {
    return this.contentGroupService.getContentGroupEntities<any>(item.id, offset, this.CONTENT_PAGER_LIMIT).pipe(
      mergeMap(res => {
        const nextItem = res.items.find(item => item.progress !== 100);
        
        if (!nextItem && (res._meta.maxcount > offset + this.CONTENT_PAGER_LIMIT)) {
          return this.getNextContentGroupItem(item, offset + this.CONTENT_PAGER_LIMIT, firstItem || res.items[0]);
        }
        
        return of(nextItem || firstItem);
      })
    );
  }

  public getNextStepIndexForPath(path: LearningPathModel, skipOptionalContent = true, afterStepId: string = null): any {
    if (!path?.contents?.length) {
      return 0;
    }

    const afterStepIndex = afterStepId ? path.contents.findIndex(step => step.id === afterStepId) : -1;
    const startIndex = afterStepIndex === -1 ? 0 : (afterStepIndex + 1);

    const nextIndex = path.contents.slice(startIndex).findIndex(step => {
      const isWebinarCompleted = step.bundle === BundleType.WEBINAR && step?.completedChild?.dateOfCompletion;

      if ((skipOptionalContent && step.nonMandatory) || isWebinarCompleted) {
        return false;
      }

      return step.progress !== 100 && !step.completedMandatory;
    });

    if (nextIndex === -1) {
      return startIndex;
    }

    return startIndex + nextIndex;
  }

  public getStepLessons(step: NodeModel): Observable<LessonDTO[]> {
    const paramName = step.bundle === BundleType.COURSE ? 'parentCourse' : 'parentModule';
    
    const params = new HttpParams()
        .set(paramName, step.uuid)
        .set('active', 'true')
        .set(interceptorsToken.BYPASS_CONTENT_VISIBILITY, 'true')
        .set('sort', 'weight:ASC');

    return this.lessonService.getAllLessons(null, params);
  }
  
  private navigateToNextStep(step: any, data: LearningPathModel, loading: BehaviorSubject<boolean>): void {
    if (step.payable && !step.paid) {
      this.payableNodeNavigationService.navigate(step);
      loading.next(false)
      return;
    }

    if (step.bundle === BundleType.COURSE || step.bundle === BundleType.MODULE) {
      loading.next(true);
      this.getStepLessons(step).pipe(
        finalize(() => loading.next(false))
      ).subscribe(lessons => {
        const nextLesson = lessons.find(item => item.progress !== 100) || lessons[0];
        this.router.navigate(['/chapters'], {
          queryParams: {
            lessonID: nextLesson.uuid,
            courseID: nextLesson.parentCourseId,
            from: NavigateFrom.LEARNING_PATH,
            fromId: data.uuid
          }
        })
      })

      return;
    }

    if (step.bundle === BundleType.WEBINAR) {
      const navigateToEvent = (id: string) => this.router.navigate(['/webinars', id]);

      if (step.isParent && +step?.childrenAmount !== 0) {
        this.webinarService.getLinkedWebinars(step.id)
          .subscribe(data => navigateToEvent(data?.items?.[0]?.uuid));

        return;
      }

      navigateToEvent(step.uuid);
      return;
    }
    
    if (step.entityTypeId === EntityTypeId.CONTENT_GROUP) {
      this.handleContentGroupNavigation(step, data, loading);
      return;
    }
    
    this.router.navigate(['/chapters'], {
      queryParams: {
        lessonID: step.uuid,
        courseID: step.parentCourseId,
        from: NavigateFrom.LEARNING_PATH,
        fromId: data.uuid
      }
    })
  }

  private handleContentGroupNavigation(step: NodeModel, data: LearningPathModel, loading: BehaviorSubject<boolean>): void {
    this.getNextContentGroupItem(step)
      .pipe(
        tap(contentGroupItem => {
          if (contentGroupItem) {
            this.navigateToNextStep(contentGroupItem, data, loading);
            return;
          }

          // No items inside group, navigate to next path step after the group
          const nextStep = this.getPathNextStep(data, step.id);
          this.navigateToNextStep(nextStep, data, loading)
        })
      )
      .subscribe();
  }

  private getPathNextStep(data: LearningPathModel, afterStepId = null): NodeModel {
    const nextStepIndex = data && this.getNextStepIndexForPath(data, false, afterStepId);

    return data.contents?.[nextStepIndex];
  }
}
